import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { NewCustomerPreview } from '../state/bill-ai-flow.models';
import { CustomerServiceV4 } from '../../../../zentility/_shared/_services/v4/customer-v4.service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-bill-ai-flow-customer-step',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatRadioModule],
  templateUrl: './bill-ai-flow-customer-step.component.html',
  styleUrl: './bill-ai-flow-customer-step.component.scss'
})
export class BillAiFlowCustomerStepComponent implements OnInit {
  public state = inject(BillAiFlowStore);
  private customerService = inject(CustomerServiceV4);

  isCreatingCustomer = false;

  // Pre-computed new customer preview (avoids method calls in template)
  newCustomerPreview: NewCustomerPreview = { suggestedName: '', billingAddress: null, legalEntityNames: [], serviceAddressCount: 0, meterCount: 0, billCount: 0 };

  ngOnInit(): void {
    this.newCustomerPreview = this.state.getNewCustomerPreview();
  }

  selectCustomer(id: number | string | null, label: string): void {
    this.state.setSelectedCustomerId(id);
    this.state.setSelectedCustomerLabel(label);
  }

  async confirmCustomer(): Promise<void> {
    const custId = this.state.selectedCustomerId();

    if (custId === null) {
      // "Create New Customer" — create via API upfront so we have a real customerId
      const details = this.state.getEditableCustomerDetails();
      this.state.setSelectedCustomerLabel(details.customerName || this.newCustomerPreview.suggestedName);

      try {
        this.isCreatingCustomer = true;
        this.state.setError(null);

        const request = this.state.getNewCustomerRequest();
        const result = await firstValueFrom(this.customerService.createCustomer(request).pipe(take(1)));

        if (!result) {
          this.state.setError('Failed to create customer: empty response');
          return;
        }

        // Set the real customer ID but mark as created-upfront so cascade still treats as new
        this.state.setSelectedCustomerId(result.customerId);
        this.state.setCreatedCustomerUpfront(true);
        this.state.initializeSelectionsAllNew();
      } catch (err: any) {
        const errorMsg = err?.error?.message || err?.message || 'Failed to create customer';
        this.state.setError(errorMsg);
        return;
      } finally {
        this.isCreatingCustomer = false;
      }
    } else {
      // Existing customer selected — re-run match with customerId
      await this.state.runMatch(Number(custId));
      if (this.state.error()) return;
    }

    this.state.completeStep('customer');
    // Portfolio-flow timing: analyzeFastTrack runs once right after the
    // initial match response, but at PORTFOLIO level the user's new-vs-
    // existing customer decision happens here — AFTER analysis. Re-run the
    // analyzer so the cascade rule (new customer → descendants unambiguously
    // new) takes effect in pause banners and row highlights downstream.
    this.state.analyzeFastTrack();
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  goBack(): void {
    this.state.goBack();
  }
}
